import { useDeleteMediaLibFileMutation } from "@api/media-library";
import Spinner from "@components/ui/spinner";
import { MediaLibDialogBase } from "@editor/modules/media-lib";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { toast } from "react-toastify";
import Button from "@/components/ui/button";
import { track } from "@/features/analytics";
import type { IMediaFile } from "@/shared/types";

const DIALOG_ID = "media-lib-delete-file";

interface IProps {
  fileData: IMediaFile;
  onRemoveFile: (id: string) => void;
}

export const MediaLibFileDeleteDialog = ({
  fileData,
  onRemoveFile,
}: IProps) => {
  const dispatch = useAppDispatch();

  const [deleteFile, { isLoading }] = useDeleteMediaLibFileMutation();

  const handleClose = () => {
    dispatch(closeDialog(DIALOG_ID));
  };

  const handleSubmit = async () => {
    if (isLoading) return;

    try {
      const respData = await deleteFile({
        id: fileData._id,
      }).unwrap();
      if (respData?.success) {
        onRemoveFile(fileData._id);
      }
    } catch (e) {
      console.log(e);
      track("error deleting media file", {
        fileData,
        error: e,
      });
      toast.error("Error deleting file");
    }

    handleClose();
  };

  return (
    <MediaLibDialogBase>
      <MediaLibDialogBase.Header title="Delete file" onClose={handleClose} />
      <MediaLibDialogBase.Content>
        <p className="tw-text-[13px]">
          Are you sure you want to delete this media for ever?
        </p>
      </MediaLibDialogBase.Content>
      <MediaLibDialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          variant="destructive"
          onClick={handleSubmit}
          className={cn(isLoading && "tw-cursor-not-allowed")}
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Deleting...
            </>
          ) : (
            "Yes, delete"
          )}
        </Button>
      </MediaLibDialogBase.Footer>
    </MediaLibDialogBase>
  );
};

MediaLibFileDeleteDialog.dialogId = DIALOG_ID;
